import { TreeRuleConstant } from "../Common/TreeRuleConstant";
import { IColorElem } from "../../../Common/IColorElem";
import { IConfigStorage } from "../../../Common/IConfigStorage";
import { DataUtilities } from "../../../Common/DataUtilities";
import { ExpressionConfiguration } from "../../../Expressions/ExpressionConfiguration";
import { DataDefinition } from "../../../Common/Data/DataDefinition";
import { BooleanDataDefinition } from "../../../Common/Data/Definitions/BooleanDataDefinition";
import { ExpressionValueType } from "../../../Expressions/ExpressionValueType";
import { IDataDefinition } from "../../../Common/Data/Definitions/IDataDefinition";
import { ValidationIssue } from "../../../Common/IDesignValidationProvider";
import { UIStyleConstants } from "../../../Common/UIStyleConstants";

export enum TreeRuleNodeType {
    EVALUATION = "evaluation",
    RESULT = "result",
    UNDEFINED = "undefined",
}

export class TreeRuleNode implements IColorElem, IConfigStorage {
    _id: string = DataUtilities.generateUniqueId();

    nodeType: TreeRuleNodeType = TreeRuleNodeType.UNDEFINED;
    color: string = UIStyleConstants.WHITE_COLOR;
    // if it is a evaluation node, expression returns true or false.  if it is a result node, expression returns the type expected
    expression: ExpressionConfiguration = new ExpressionConfiguration();

    private _trueNode: TreeRuleNode | undefined;

    get trueNode(): TreeRuleNode | undefined {
        if (this.nodeType === TreeRuleNodeType.EVALUATION && !this._trueNode) this._trueNode = new TreeRuleNode();

        return this._trueNode;
    }

    // expression

    set trueNode(treeNode: TreeRuleNode | undefined) {
        this._trueNode = treeNode;
    }

    private _falseNode: TreeRuleNode | undefined;

    get falseNode(): TreeRuleNode | undefined {
        if (this.nodeType === TreeRuleNodeType.EVALUATION && !this._falseNode) this._falseNode = new TreeRuleNode();

        return this._falseNode;
    }

    set falseNode(treeNode: TreeRuleNode | undefined) {
        this._falseNode = treeNode;
    }

    private _missingDataNode: TreeRuleNode | undefined;

    set missingDataNode(value: TreeRuleNode | undefined) {
        if (this.nodeType === TreeRuleNodeType.EVALUATION && !this._missingDataNode)
            this._missingDataNode = new TreeRuleNode();

        this._missingDataNode = value;
    }

    setupEvaluation(datadef: DataDefinition) {
        this.nodeType = TreeRuleNodeType.EVALUATION;
        let booleandef = new BooleanDataDefinition();

        this.expression.executionResult = () => {
            return booleandef;
        };
        this.expression.inputData = () => {
            return datadef;
        };
        this.expression.valueType = ExpressionValueType.SUBEXPRESSION;
    }

    setupResult(idatadef: DataDefinition, outdef: IDataDefinition) {
        this.nodeType = TreeRuleNodeType.RESULT;
        this.trueNode = undefined;
        this.falseNode = undefined;

        this.expression.valueType = ExpressionValueType.VALUE;

        this.expression.executionResult = () => {
            return outdef;
        };

        this.expression.inputData = () => {
            return idatadef;
        };

        this.color = TreeRuleConstant.DEFAULT_RESULT_NODE_COLOR;
    }

    delete(): void {
        if (
            this.trueNode &&
            (this.trueNode?.nodeType === TreeRuleNodeType.RESULT ||
                this.trueNode?.nodeType === TreeRuleNodeType.EVALUATION)
        ) {
            this.trueNode.delete();
            delete this.trueNode;
        }
        if (
            this.falseNode &&
            (this.falseNode?.nodeType === TreeRuleNodeType.RESULT ||
                this.falseNode?.nodeType === TreeRuleNodeType.EVALUATION)
        ) {
            this.falseNode.delete();
            delete this.falseNode;
        }

        this.nodeType = TreeRuleNodeType.UNDEFINED;
        this.falseNode = undefined;
        this.trueNode = undefined;
    }

    getDesignValidationIssues(): ValidationIssue[] {
        if (this.nodeType === TreeRuleNodeType.UNDEFINED) {
            return [new ValidationIssue("Node is required.")];
        }
        return this.expression.getDesignValidationIssues();
    }

    getStorageData(): object {
        let data = {};

        Reflect.set(data, "_id", this._id);
        Reflect.set(data, "nodeType", this.nodeType);

        if (this.trueNode) {
            Reflect.set(data, "trueNode", this.trueNode.getStorageData());
        }

        if (this.falseNode) {
            Reflect.set(data, "falseNode", this.falseNode.getStorageData());
        }

        if (this.missingDataNode) {
            Reflect.set(data, "missingDataNode", this.missingDataNode.getStorageData());
        }

        Reflect.set(data, "expression", this.expression.getStorageData());

        return data;
    }

    setStorageData(data: object) {
        this._id = Reflect.get(data, "_id");
        this.nodeType = Reflect.get(data, "nodeType");

        let trueNodeData = Reflect.get(data, "trueNode");
        if (trueNodeData) {
            let node = new TreeRuleNode();
            node.setStorageData(trueNodeData);
            this.trueNode = node;
        }

        let falseNodeData = Reflect.get(data, "falseNode");
        if (falseNodeData) {
            let node = new TreeRuleNode();
            node.setStorageData(falseNodeData);
            this.falseNode = node;
        }

        let missingDataNodeData = Reflect.get(data, "missingDataNode");
        if (missingDataNodeData) {
            let node = new TreeRuleNode();
            node.setStorageData(missingDataNodeData);
            this.missingDataNode = node;
        }

        let expressionData = Reflect.get(data, "expression");

        this.expression.setStorageData(expressionData);
    }
}
